import React, { useEffect, useMemo, useRef, useState } from 'react';
import proj4 from 'proj4';
import apiConfig from '../config/api';
import './AreaActivity.css';

// Urban-renewal activity around the property (Tel Aviv only): TAMA 38 /
// demolish-rebuild permit requests within a ground radius, classified by their
// furthest milestone. Backed by /api/govmap-layers?action=area-activity
// (TA GIS permits layer 772 — municipal APIs are always proxied server-side).
//
// Layout per the approved design ("Urban Renewal Section" / claude.ai/design):
// header with summary, status chips that are BOTH legend and filters, and a
// split pane — distance-sorted scrollable list beside an interactive map with
// two-way selection sync and a callout card for the selected project.

const STATUS_META = {
  building: { label: 'בבנייה', color: '#2f6fe4', bg: '#e8f0fe', fg: '#1a56c9' },
  in_licensing: { label: 'בתהליך רישוי', color: '#e8960c', bg: '#fdf1dc', fg: '#9a5b00' },
  permitted: { label: 'ניתן היתר', color: '#0e9f8a', bg: '#dff5f0', fg: '#0b6e60' },
  completed: { label: 'הושלם', color: '#34a853', bg: '#e4f4e6', fg: '#1e7d34' },
};
const CHIP_ORDER = ['building', 'in_licensing', 'permitted', 'completed'];

// Accepts ISO (YYYY-MM-DD, from the GIS-Net path) or DD/MM/YYYY (Tel Aviv path)
// → DD.MM.YY, so dates read consistently regardless of source.
const fmtIso = (v) => {
  if (!v) return null;
  const s = String(v);
  let y; let m; let d;
  if (/^\d{4}-\d{1,2}-\d{1,2}/.test(s)) { [y, m, d] = s.split('-'); }
  else if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(s)) { [d, m, y] = s.split('/'); }
  else return s;
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${String(y).slice(2)}`;
};

// The search pipeline hands coordinates in either Web Mercator (from the
// address geocoder) or ITM (parcel flows) — same detection as LocationMap.
function toMercator(coordinates) {
  const x = Number(coordinates?.x);
  const y = Number(coordinates?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x > 3000000 && y > 3000000) return { x, y };
  if (x > 100000 && x < 300000 && y > 300000 && y < 1000000) {
    try {
      const [lng, lat] = proj4('EPSG:2039', 'EPSG:4326', [x, y]);
      const [mx, my] = proj4('EPSG:4326', 'EPSG:3857', [lng, lat]);
      return { x: mx, y: my };
    } catch {
      return null;
    }
  }
  return null;
}

const mercToLL = (pt) => {
  const [lng, lat] = proj4('EPSG:3857', 'EPSG:4326', [pt.x, pt.y]);
  return { lat, lng };
};

const keyDate = (p) => {
  if (p.status === 'completed') return p.finishedDate ? `הסתיים ${fmtIso(p.finishedDate)}` : 'הסתיים';
  if (p.status === 'building') {
    const d = fmtIso(p.startDate || p.tofes4Date);
    return d ? `העבודות החלו ${d}` : 'בבנייה';
  }
  if (p.status === 'permitted') return p.permitDate ? `היתר ניתן ${fmtIso(p.permitDate)}` : 'היתר ניתן';
  return p.opened ? `הבקשה הוגשה ${fmtIso(p.opened)}` : 'בבדיקה';
};

// Row meta WITHOUT the distance — the distance is promoted to its own chip in
// the row's top line so proximity is scannable; the callout keeps it inline.
const projectMeta = (p, { withDistance = false } = {}) => [
  withDistance && p.distanceM != null ? `כ־${p.distanceM} מ׳ מהנכס` : null,
  p.isTama38 ? 'תמ״א 38' : null,
  p.isDemolishRebuild ? 'הריסה ובנייה' : 'תוספת בנייה',
  p.units ? `${p.units} יח״ד` : null,
  keyDate(p),
].filter(Boolean).join(' · ');

const projectId = (p) => String(p.requestNum || p.address);

// Interactive map pane: property pin + radius ring + a colored circle marker
// per project. Selection enlarges/glows the marker; an active filter dims the
// non-matching ones (the design's schematic behavior, on the real map).
function AreaMap({ merc, projects, radius, selected, filter, onSelect }) {
  const mapRef = useRef(null);
  const stateRef = useRef(null); // { map, markers: Map<id, {marker, status}> }
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    const g = window.google?.maps;
    if (!mapRef.current || !g) return undefined;
    const center = mercToLL(merc);
    const map = new g.Map(mapRef.current, {
      center,
      zoom: 17,
      mapTypeControl: false,
      streetViewControl: false,
      clickableIcons: false,
      gestureHandling: 'cooperative',
    });
    const extras = [];
    extras.push(new g.Marker({ map, position: center, title: 'הנכס הנבדק', zIndex: 1000 }));
    extras.push(new g.Circle({
      map,
      center,
      radius,
      fillColor: '#94a3b8',
      fillOpacity: 0.07,
      strokeColor: '#9aa6b5',
      strokeOpacity: 0.6,
      strokeWeight: 1.5,
    }));
    const markers = new Map();
    for (const p of projects) {
      if (!p.centroid) continue;
      const id = projectId(p);
      const marker = new g.Marker({ map, position: mercToLL(p.centroid), title: p.address || '' });
      marker.addListener('click', () => onSelectRef.current(id));
      markers.set(id, { marker, status: p.status, position: mercToLL(p.centroid) });
    }
    stateRef.current = { map, markers, g };
    return () => {
      extras.forEach((o) => o.setMap(null));
      markers.forEach(({ marker }) => marker.setMap(null));
      stateRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merc?.x, merc?.y, projects, radius]);

  // Restyle markers on selection/filter change without rebuilding the map.
  useEffect(() => {
    const s = stateRef.current;
    if (!s) return;
    const { g } = s;
    s.markers.forEach(({ marker, status, position }, id) => {
      const isSel = selected === id;
      const matches = !filter || status === filter;
      marker.setIcon({
        path: g.SymbolPath.CIRCLE,
        scale: isSel ? 13 : 9,
        fillColor: (STATUS_META[status] || STATUS_META.in_licensing).color,
        fillOpacity: matches || isSel ? 0.95 : 0.22,
        strokeColor: '#ffffff',
        strokeWeight: isSel ? 3 : 2,
      });
      marker.setZIndex(isSel ? 500 : (matches ? 100 : 10));
      if (isSel) s.map.panTo(position);
    });
  }, [selected, filter, projects]);

  return <div ref={mapRef} className="aa-map" role="application" aria-label="מפה אינטראקטיבית של פרויקטי התחדשות בסביבה" />;
}

// Municipalities the radar covers today: Tel Aviv (direct GIS) + GIS-Net cities
// (proxied server-side). Kept here only to avoid firing a request that the
// backend would 404/502; the server is the source of truth.
const SUPPORTED = [/תל אביב/, /רמת גן/];

const AreaActivity = ({ coordinates, municipality, radius = 150 }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState(null);
  const listRef = useRef(null);
  const isSupported = SUPPORTED.some((re) => re.test(String(municipality || '')));
  const merc = toMercator(coordinates);

  useEffect(() => {
    if (!isSupported || !merc) return undefined;
    let alive = true;
    const ctrl = new AbortController();
    setState('loading');
    const muni = encodeURIComponent(String(municipality || ''));
    fetch(`${apiConfig.apiBaseUrl}/api/govmap-layers?action=area-activity&x=${merc.x}&y=${merc.y}&radius=${radius}&municipality=${muni}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (alive) { setData(d); setState('done'); } })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported, merc?.x, merc?.y, radius, municipality]);

  // Distance-sorted (the design: one list, nearest first, status via pill).
  const projects = useMemo(
    () => [...(data?.projects || [])].sort((a, b) => (a.distanceM ?? 1e9) - (b.distanceM ?? 1e9)),
    [data]
  );

  // Selecting (from map or list) scrolls the list to the row.
  const select = (id) => {
    setSelected((cur) => (cur === id ? cur : id));
    const row = document.getElementById(`aa-row-${id}`);
    const list = listRef.current;
    if (row && list) list.scrollTo({ top: row.offsetTop - list.offsetTop - 8, behavior: 'smooth' });
  };

  if (!isSupported || !merc) return null;

  const counts = projects.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});
  const active = projects.length - (counts.completed || 0);
  const listRows = filter ? projects.filter((p) => p.status === filter) : projects;
  const selProj = selected != null ? projects.find((p) => projectId(p) === selected) : null;
  const hasMapsJs = typeof window !== 'undefined' && Boolean(window.google?.maps);

  return (
    <div className="market-data-card real-data aa-card">
      {/* Standard card header — same language as ריכוז נתונים / נתוני שוק. */}
      <div className="market-data-header">
        <h3 className="market-data-title"><i className="fi fi-rr-city"></i> התחדשות עירונית בסביבת הנכס</h3>
        <span className="aa-radius">רדיוס כ־{radius} מ׳ · רישוי {municipality || 'עירוני'}</span>
      </div>

      {state === 'loading' && (
        <p className="aa-note"><span className="spinner-small"></span> בודק בקשות והיתרים בסביבה…</p>
      )}
      {state === 'error' && (
        <p className="aa-note">לא הצלחנו לשלוף כעת את נתוני הרישוי הסביבתיים. נסו לרענן.</p>
      )}
      {state === 'done' && projects.length === 0 && (
        <p className="aa-note">לא אותרה פעילות התחדשות (תמ״א 38 / הריסה ובנייה) ברדיוס שנבדק.</p>
      )}

      {state === 'done' && projects.length > 0 && (
        <>
          <p className="aa-summary">
            {active > 0
              ? <>נמצאו <strong>{active} פרויקטים פעילים</strong>{counts.completed ? ` ועוד ${counts.completed} שהושלמו` : ''} בסביבת הנכס.</>
              : <>נמצאו {counts.completed} פרויקטים שהושלמו בסביבת הנכס.</>}
          </p>
          <div className="aa-chips" role="group" aria-label="סינון לפי סטטוס">
            <button
              type="button"
              className={`aa-chip ${filter === null ? 'is-active' : ''}`}
              onClick={() => { setFilter(null); setSelected(null); }}
            >
              הכל {projects.length}
            </button>
            {CHIP_ORDER.map((s) => (
              counts[s] ? (
                <button
                  key={s}
                  type="button"
                  className={`aa-chip ${filter === s ? 'is-active' : ''}`}
                  style={filter === s ? undefined : { background: STATUS_META[s].bg, color: STATUS_META[s].fg }}
                  onClick={() => { setFilter((f) => (f === s ? null : s)); setSelected(null); }}
                  aria-pressed={filter === s}
                >
                  <span className="aa-chip-dot" style={{ background: STATUS_META[s].color }}></span>
                  {STATUS_META[s].label} {counts[s]}
                </button>
              ) : null
            ))}
            <span className="aa-chips-hint">{filter ? 'לחיצה חוזרת מנקה סינון' : 'לחצו לסינון המפה והרשימה'}</span>
          </div>

          <div className="aa-split">
            <div className="aa-listpane">
              <div className="aa-list" ref={listRef}>
                {listRows.map((p) => {
                  const id = projectId(p);
                  const st = STATUS_META[p.status] || STATUS_META.in_licensing;
                  const isSel = selected === id;
                  return (
                    <button
                      type="button"
                      id={`aa-row-${id}`}
                      key={id}
                      className={`aa-row ${isSel ? 'is-selected' : ''}`}
                      onClick={() => select(id)}
                    >
                      <span className="aa-row-top">
                        <span className="aa-row-dot" style={{ background: st.color }}></span>
                        <span className="aa-row-name">{p.address || 'כתובת לא צוינה'}</span>
                        {p.distanceM != null && <span className="aa-row-dist">{p.distanceM} מ׳</span>}
                        <span className="aa-row-pill" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                      </span>
                      <span className="aa-row-meta">{projectMeta(p)}</span>
                    </button>
                  );
                })}
                {listRows.length === 0 && (
                  <div className="aa-list-empty">אין פרויקטים בסטטוס זה בסביבת הנכס</div>
                )}
              </div>
              <div className="aa-list-foot">
                <span>{listRows.length} פרויקטים ברשימה</span>
                <span>ממוין לפי מרחק מהנכס</span>
              </div>
            </div>

            <div className="aa-mappane">
              {hasMapsJs ? (
                <AreaMap
                  merc={merc}
                  projects={projects}
                  radius={radius}
                  selected={selected}
                  filter={filter}
                  onSelect={select}
                />
              ) : (
                <div className="aa-map aa-map-unavailable">המפה אינה זמינה כרגע</div>
              )}
              {selProj && (
                <div className="aa-callout" role="status">
                  <span className="aa-row-dot" style={{ background: (STATUS_META[selProj.status] || STATUS_META.in_licensing).color }}></span>
                  <div className="aa-callout-text">
                    <div className="aa-callout-name">{selProj.address || 'כתובת לא צוינה'}</div>
                    <div className="aa-callout-meta">{projectMeta(selProj, { withDistance: true })}</div>
                  </div>
                  <button type="button" className="aa-callout-x" onClick={() => setSelected(null)} aria-label="סגירת פרטי הפרויקט">
                    <i className="fi fi-rr-cross-small"></i>
                  </button>
                </div>
              )}
              <span className="aa-map-caption">נתוני רישוי עירוניים · מוצגים פרויקטים בתחום {municipality || 'הרשות'} בלבד</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AreaActivity;
